// @flow

import React, { type ComponentType, type Node, lazy } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import SafeSuspense from 'app/packages/safe-suspense';
import type { Dispatch } from 'redux';
import type { State } from 'app/common/redux/types';
import { toastShow, toastHide } from 'app/common/redux/actions/toasts';

type ToastComponent<Props> = ComponentType<Props> | ((props: Props) => any);

const PinBuilderToast = lazy(() =>
  import(/* webpackChunkName: "PinBuilderToast" */ 'app/common/react/components/pinbuilder/toast/PinBuilderToast'),
);

const TOAST_STYLE = {
  bottom: '14px',
  left: '50%',
  marginLeft: '-183px',
  position: 'fixed',
  zIndex: 1500,
};

type OwnProps = {|
  children: Node,
  siteType: number,
|};

type StateProps = {|
  currentlyUploadingItems: boolean,
  isPartner: boolean,
  toasts: { name: string }[],
|};

type Props = {|
  ...OwnProps,
  ...StateProps,
  dispatch: Dispatch<any>,
|};

export class Toasts extends React.Component<Props> {
  static childContextTypes = {
    showToast: PropTypes.func.isRequired,
    hideAllToasts: PropTypes.func.isRequired,
  };

  getChildContext() {
    return {
      showToast: this.showToast,
      hideAllToasts: this.hideAllToasts,
    };
  }

  handleHide = (id: string) => {
    delete this.toastComponents[id];
    this.props.dispatch(toastHide(id));
  };

  hideAllToasts = () => {
    Object.keys(this.toastComponents).forEach((toastComponentId: string) => {
      this.handleHide(toastComponentId);
    });
  };

  showToast = (component: ToastComponent<*>) => {
    const id = this.nextId;
    this.nextId += 1;
    const name = component.displayName ? `${component.displayName}${id}` : `toast${id}`;
    this.toastComponents[name] = component;
    this.props.dispatch(toastShow(name));
  };

  nextId = 0;
  toastComponents: { [name: string]: ToastComponent<*> } = {};

  render() {
    const { children, currentlyUploadingItems, isPartner, siteType, toasts } = this.props;

    return (
      <div>
        {children}
        <div style={TOAST_STYLE}>
          {toasts.map(({ name }) => {
            const Toast = this.toastComponents[name];
            if (!Toast) {
              return undefined;
            }
            return <Toast key={name} onHide={() => this.handleHide(name)} />;
          })}
          {!isPartner && currentlyUploadingItems ? (
            <SafeSuspense>
              <PinBuilderToast siteType={siteType} />
            </SafeSuspense>
          ) : null}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: State, ownProps: OwnProps): StateProps => ({
  // This component is used in more than just www and some of those apps do not use the pinBuilder
  // reducer key so we need to check if the key exists before pulling more data
  currentlyUploadingItems: state.pinbuilder
    ? Object.keys(state.pinbuilder.uploadingPinDraftIdMap).length > 0
    : false,
  isPartner: state.viewer.isAuth && state.viewer.isPartner,
  toasts: state.ui.toasts,
});

export default connect<Props, OwnProps, _, _, _, _>(mapStateToProps)(Toasts);
